/**
 * BottomSheet — Carnaf design system v1.0
 * Spec: docs/strategy/car-as-place/design-system-2026-05-22/04-components.md § 3
 *
 * Primary modal pattern in Carnaf. Full-screen modals reserved for multi-step
 * flows (accident). Bottom sheet anatomy:
 *   - top-rounded 32px, bottom flush to viewport
 *   - drag handle 36×4px centered at top
 *   - optional close X button top-end
 *   - dismiss: backdrop tap, swipe-down, X button, Escape key
 *   - slide-up animation 350ms with cubic-bezier(0.32, 0.72, 0, 1)
 *
 * Accessibility: role="dialog", aria-modal="true", focus trap, Escape close.
 */

import { useEffect, useRef } from 'react';
import type { ReactNode } from 'react';

interface BottomSheetProps {
  /** Render the sheet open. Caller controls open/closed state. */
  open: boolean;
  /** Called when user wants to dismiss (backdrop tap, X, Escape). */
  onClose: () => void;
  title?: string;
  /** Render label for screen readers when `title` is omitted. */
  ariaLabel?: string;
  /** Show the close X button in the top-end corner. Default true. */
  showCloseButton?: boolean;
  children: ReactNode;
  /** Sticky CTA bar at the bottom (typically a primary Button). Optional. */
  footer?: ReactNode;
}

export function BottomSheet({
  open,
  onClose,
  title,
  ariaLabel,
  showCloseButton = true,
  children,
  footer,
}: BottomSheetProps): JSX.Element | null {
  const dialogRef = useRef<HTMLDivElement>(null);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Focus the dialog when opened
  useEffect(() => {
    if (open && dialogRef.current) {
      dialogRef.current.focus();
    }
  }, [open]);

  // Lock body scroll while open
  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-40 flex items-end animate-fade-in" aria-hidden={!open}>
      {/* Backdrop */}
      <button
        type="button"
        aria-label="סגירה"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
        // Override the global min-h/min-w 48px since this is a full-bleed backdrop
        style={{ minHeight: 0, minWidth: 0 }}
      />
      {/* Sheet */}
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-label={title ?? ariaLabel}
        tabIndex={-1}
        className="relative w-full bg-white rounded-t-3xl shadow-sheet animate-slide-up outline-none max-h-[90vh] flex flex-col"
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2 flex-shrink-0">
          <div className="w-9 h-1 bg-carnaf-line-strong rounded-full" />
        </div>
        {/* Close button */}
        {showCloseButton && (
          <button
            type="button"
            onClick={onClose}
            aria-label="סגירה"
            className="absolute top-3 end-3 w-10 h-10 flex items-center justify-center text-carnaf-ink-muted hover:text-carnaf-ink"
            style={{ minHeight: 40, minWidth: 40 }}
          >
            ✕
          </button>
        )}
        {/* Title */}
        {title && <h2 className="px-5 pt-2 pb-3 text-h2 text-carnaf-ink flex-shrink-0">{title}</h2>}
        {/* Content */}
        <div className="flex-1 overflow-y-auto px-5 pb-5">{children}</div>
        {/* Footer */}
        {footer && (
          <div
            className="flex-shrink-0 border-t border-carnaf-line px-5 py-4"
            style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
